import React, { Dispatch, SetStateAction } from 'react'
import styles from './additional-info-section.module.scss'
import { useFormContext } from 'react-hook-form'
import { InputRedesigned, Textarea, Typography } from '@/ui-kit'
import { FieldBlock } from '../field-block'
import { MyProfile } from '@/types/profile'
import { DropdownIndicator } from '@/assets/icons'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { ProfileEditSchemaType } from '@/schemes/profile-edit-schema'
import { ModalField } from '../modal-field'

interface Props {
    userData: MyProfile
    handleUpdateUserData: (data: any, needToast?: boolean, onSuccess?: () => void) => Promise<void>
    collectEditEvent: (field: string) => void
}

export const AdditionalInfoSection: React.FC<Props> = ({
    userData,
    handleUpdateUserData,
    collectEditEvent,
}) => {
    const { t } = useTranslation()

    const methods = useFormContext<ProfileEditSchemaType>()

    const {
        register,
        formState: { errors }
    } = methods

    const additionalInfo = userData.additional_info
    
    return (
        <>
            <Typography
                variant="span"
                fontWeight="semibold"
                fontFamily="SFProText"
                className={styles.sectionTitle}>
                {t('profile.tell_me_more_about_yourself')}
            </Typography>

            <Typography
                variant="p"
                fontWeight="semibold"
                fontFamily="SFProText"
                className={styles.sectionSubtitle}>
                {t('profile.tell_me_more_about_yourself_note')}
            </Typography>

            <FieldBlock
                id="catch_my_interest"
                error={!!errors?.catch_my_interest?.message}>
                <Textarea
                    label={t('catch_my_interest.title')}
                    maxLength={255}
                    defaultValue={additionalInfo?.catch_my_interest ?? ''}
                    className={styles.textarea}
                    {...register('catch_my_interest', { required: false })} />
            </FieldBlock>

            <FieldBlock
                id="my_ideal_evening"
                error={!!errors?.my_ideal_evening?.message}>
                <Textarea
                    label={t('my_ideal_evening.title')}
                    maxLength={255}
                    defaultValue={additionalInfo?.my_ideal_evening ?? ''}
                    className={styles.textarea}
                    {...register('my_ideal_evening', { required: false })} />
            </FieldBlock>

            <div className={clsx(styles.oneLineField, styles.mb6)}>
                <InputRedesigned
                    placeholder={t('height.title')}
                    label={t('height.title')}
                    error={errors?.height?.message}
                    {...register('height', { required: false })} />
                {
                    errors?.height?.message &&
                    <p className={styles.error}>{t(errors.height.message)}</p>
                }
            </div>

            <ModalField
                userData={userData}
                field="marital_status"
                values={[
                    'single',
                    'divorced',
                    'widowed',
                    'not_specified',
                ]}
                defaultVal={userData.additional_info?.marital_status}
                handleUpdateUserData={handleUpdateUserData}
                collectEditEvent={collectEditEvent} />

            <ModalField
                userData={userData}
                field="smoking"
                values={[
                    'non_smoker',
                    'smoker',
                    'occasionally',
                ]}
                defaultVal={userData.additional_info?.smoking}
                handleUpdateUserData={handleUpdateUserData}
                collectEditEvent={collectEditEvent} />

            <ModalField
                userData={userData}
                field="drinking"
                values={[
                    'non_drinker',
                    'occasionally',
                    'i_drink',
                ]}
                defaultVal={userData.additional_info?.drinking}
                handleUpdateUserData={handleUpdateUserData}
                collectEditEvent={collectEditEvent} />

            <ModalField
                userData={userData}
                field="children"
                values={[
                    'none',
                    'live_with_me',
                    'live_separately'
                ]}
                defaultVal={userData.additional_info?.children}
                handleUpdateUserData={handleUpdateUserData}
                collectEditEvent={collectEditEvent} />

            <div className={styles.oneLineField}>
                <InputRedesigned
                    placeholder={t('language_proficiency.title')}
                    label={t('language_proficiency.title')}
                    error={errors?.language_proficiency?.message}
                    {...register('language_proficiency', { required: false })} />
                {
                    errors?.language_proficiency?.message &&
                    <p className={styles.error}>{t(errors.language_proficiency.message)}</p>
                }
            </div>
        </>
    )
}
